import type { ChatMention } from '../types';

export type MentionCandidate = {
  id: string;
  name: string;
  image: string | null;
};

export type AppliedMention = {
  text: string;
  mentions: ChatMention[];
  /** Where the caret belongs afterwards -- just past the inserted name and its space. */
  caret: number;
};

/**
 * Replace the `@query` the user is typing with `@Name ` and record where it landed.
 *
 * The recorded span INCLUDES the leading `@`, so
 * `text.slice(start, start + length) === '@' + name` always holds. Every other function
 * here leans on that invariant to tell a live mention from one the user has since edited
 * into something else.
 */
export function applyMention(
  text: string,
  mentions: ChatMention[],
  queryStart: number,
  caret: number,
  candidate: MentionCandidate,
): AppliedMention {
  const token = `@${candidate.name}`;
  const replacement = `${token} `;
  const next = text.slice(0, queryStart) + replacement + text.slice(caret);
  const delta = replacement.length - (caret - queryStart);

  const shifted = mentions
    // Anything overlapping the region we just overwrote is gone, by definition.
    .filter((m) => m.start + m.length <= queryStart || m.start >= caret)
    .map((m) => (m.start >= caret ? { ...m, start: m.start + delta } : m));

  shifted.push({
    user_id: candidate.id,
    name: candidate.name,
    start: queryStart,
    length: token.length,
  });
  shifted.sort((a, b) => a.start - b.start);

  return { text: next, mentions: shifted, caret: queryStart + replacement.length };
}
